// En démo publique, l'horloge est figée AVANT que quoi que ce soit lise la date.
import { estDemo } from './demo/horloge';
import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './state/auth';
import App from './App';
import './styles.css';
import { demarrerSynchronisation } from './lib/offline';
import { appliquerLangue, langueInitiale, memoriserLangue, type Langue } from './i18n';
import { ContexteLangue } from './i18n/contexte';
import { urlApi } from './api/config';
import { setToken } from './api/client';
import { chargerJeu, installerFetchDemo, jeuPourLangue, roleChoisi } from './demo/rejeu';
import { EntreeDemo } from './demo/EntreeDemo';
import { BandeauDemo } from './demo/BandeauDemo';
import './demo/demo.css';

// Pas de service worker en démo : chaque visite doit voir la dernière version.
if ('serviceWorker' in navigator && import.meta.env.PROD && !estDemo) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

demarrerSynchronisation();

/**
 * La racine porte la langue.
 *
 * Changer de langue REMONTE toute l'application (clé React) : chaque écran lit
 * ses textes par `T.<écran>` au rendu, et un remontage est la seule façon sûre
 * qu'aucun texte, aucune liste calculée, aucun badge ne reste dans l'ancienne
 * langue. C'est un geste rare : on accepte de recharger les données.
 */
function Racine({ initiale }: { initiale: Langue }) {
  const [langue, setLangue] = useState<Langue>(initiale);
  const changer = (l: Langue) => {
    memoriserLangue(l);
    // En démo, la langue choisit aussi l'agence (Lisbonne / Lyon) : on recharge le bon jeu.
    if (estDemo) { window.location.reload(); return; }
    appliquerLangue(l);
    setLangue(l);
  };
  return (
    <ContexteLangue.Provider value={{ langue, changer }}>
      {estDemo && !roleChoisi() ? <EntreeDemo /> : (
        <>
          {estDemo && <BandeauDemo />}
          <BrowserRouter key={langue}>
            <AuthProvider><App /></AuthProvider>
          </BrowserRouter>
        </>
      )}
    </ContexteLangue.Provider>
  );
}

/*
 * Les réglages régionaux de l'agence (locale, devise) se lisent AVANT le
 * premier affichage : sinon une agence australienne verrait clignoter des
 * dinars. La route est publique ; si le serveur tarde, on démarre quand même
 * au bout d'une seconde et demie avec ce qu'on sait.
 */
async function demarrer() {
  /*
   * DÉMO : la langue du visiteur choisit l'agence (Lisbonne en anglais, Lyon en
   * français) ; on charge son enregistrement AVANT tout appel, et tous les
   * appels /api/ passent par le rejeu.
   */
  if (estDemo) {
    const role = roleChoisi();
    await chargerJeu(jeuPourLangue(langueInitiale()), role);
    installerFetchDemo();
    setToken(role ? 'demo' : null);
  }

  let reglages: { locale?: string; devise?: string; uniteDistance?: 'km' | 'mi' } = {};
  try {
    const ctrl = new AbortController();
    const delai = setTimeout(() => ctrl.abort(), 1500);
    const r = await fetch(urlApi('/api/auth/identite'), { signal: ctrl.signal });
    clearTimeout(delai);
    if (r.ok) reglages = await r.json();
  } catch { /* hors ligne ou serveur lent : on démarre avec les valeurs connues */ }

  const l = langueInitiale(reglages.locale);
  appliquerLangue(l, {
    locale: reglages.locale ?? 'fr-DZ',
    devise: reglages.devise ?? 'DZD',
    uniteDistance: reglages.uniteDistance ?? 'km',
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode><Racine initiale={l} /></React.StrictMode>,
  );
}

void demarrer();
